/* ============================================================================
   COOPER PROJECT — THE MOTION SCRIPT
   ----------------------------------------------------------------------------
   Two things: one shared IntersectionObserver that flips [data-shown], and the
   pointer-light loop. No dependencies, no build step.

   LOAD IT WITH defer, NOT async. The gate attribute below has to be on <html>
   before first paint or every revealed block flashes visible and then hides —
   the section that uses this writes the attribute inline for exactly that
   reason, and this file re-writes it so the asset also works alone.
   ========================================================================= */
(() => {
  document.documentElement.setAttribute('data-cooper-js', '');

  /* --------------------------------------------------------------------------
     THE REVEAL OBSERVER — one for the whole document, shared by every element.

     THE CALLBACK SETS ONE ATTRIBUTE AND NEVER READS LAYOUT beyond the rect the
     entry already carries. A callback that measures is how a reveal turns into
     a scroll-jank generator.

     threshold [0, 0.2]: 0.2 is the template's own. THE EXTRA 0 IS NOT
     DECORATION — intersectionRatio is a fraction of the ELEMENT, so a block
     three times the viewport height can never reach 0.2 and would stay
     invisible forever. Hence the `tall` test.

     IT REPLAYS rather than unobserving on first fire, because the template's
     own export carries "replay": true on every in-view effect.
     ----------------------------------------------------------------------- */
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const tall = entry.boundingClientRect.height > innerHeight * 0.8;
        const shown =
          entry.isIntersecting && (tall || entry.intersectionRatio >= 0.2);
        entry.target.dataset.shown = shown ? 'true' : 'false';
      }
    },
    {threshold: [0, 0.2]},
  );

  /* WHY A WeakSet AND NOT AN ATTRIBUTE, which is what this used to be.

     The old marker was `el.dataset.revealBound = ''`, written at the top of the
     loop below, and the selector filtered on `:not([data-reveal-bound])`. It
     worked, and it cost a forced reflow per element: an attribute write dirties
     that element's style, and the very next statement read
     getBoundingClientRect(), which cannot answer without flushing layout again.
     One flush per reveal, and there are dozens on the home page. Chrome named
     bindReveals at 61-75ms of forced layout in two independent traces.

     A WeakSet marks the same elements with no attribute, so no style
     invalidation, so nothing to flush. Nothing else in the theme reads
     data-reveal-bound — grepped, not assumed — so the attribute had no other
     job. It is also not needed for correctness on its own: observing an already
     observed element is a no-op. It exists to skip repeated work. */
  const revealBound = new WeakSet();

  function bindReveals(root) {
    const scope = root instanceof Element ? root : document;
    const fresh = [];
    for (const el of scope.querySelectorAll('.reveal, .reveal-words')) {
      if (revealBound.has(el)) continue;
      revealBound.add(el);
      fresh.push(el);
    }
    if (!fresh.length) return;

    /* READ PHASE, ALL OF IT, BEFORE ANY WRITE. The first rect below flushes
       layout once; every rect after it is free, because nothing between them
       dirties anything. This is the whole fix — the numbers are identical to
       the old loop's, they are just no longer interleaved with writes.

       innerHeight is hoisted for the same reason: it is a layout read too. */
    const fold = innerHeight * 0.85;
    const above = [];
    for (const el of fresh) {
      /* ABOVE-THE-FOLD SHORTCUT: show it now rather than waiting for a scroll
         that may never come — but keep observing, so it can replay on the way
         back up. */
      if (el.getBoundingClientRect().top < fold) above.push(el);
    }

    /* WRITE PHASE. `data-shown` is a live selector in cooper-motion.css, so
       these writes do dirty style — which is exactly why they come last, when
       there is no read left to be forced by them. */
    for (const el of above) el.dataset.shown = 'true';
    for (const el of fresh) io.observe(el);
  }

  /* --------------------------------------------------------------------------
     THE POINTER LIGHT.

     THE LIGHT LEADS AND THE DOG FOLLOWS. This lerps at 0.10 per frame, which
     settles in about 400ms at 60Hz; the corgi tracks the same pointer more
     slowly (damping 6). The lag between them is what reads as a lit object
     rather than a sticker with a glow behind it. Change this number and you are
     changing that relationship.

     BOTH MEDIA QUERIES ARE WATCHED LIVE, not read once. Read once, somebody who
     switched reduced motion ON with the page open kept a light chasing their
     cursor while the corgi beside it — which always watched the query live —
     had already stopped: two halves of one band disagreeing about one operating
     system setting, in the direction that ignores a request to stop animating.
     (hover: none) is watched too, because it flips in real life when a mouse is
     plugged into or pulled out of a tablet.

     EXPONENTIAL APPROACH, NOT A SPRING. No overshoot anywhere in this system.
     ----------------------------------------------------------------------- */
  function bindCursorLight(el) {
    if (el.dataset.lightBound !== undefined) return;
    el.dataset.lightBound = '';

    const host = el.parentElement;
    if (!host) return;

    const touch = matchMedia('(hover: none)');
    const still = matchMedia('(prefers-reduced-motion: reduce)');

    let raf = 0;
    let running = false;
    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;

    /* THE POINTER HANDLER TOUCHES NO GEOMETRY, AND THAT IS THE FIX.

       It used to read host.getBoundingClientRect() on every pointermove while
       tick wrote el.style.transform on every frame. Those two interleave for as
       long as somebody is moving the mouse — write, read, write, read — and every
       read after a write has to flush layout. PSI attributes about 94 ms of
       forced layout to this file across six call sites; this is the pair.

       Now the event only remembers where the pointer was. The rect is read
       inside the frame instead, BEFORE the write, so the order within a frame is
       read-then-write and there is nothing left to force.

       THE FLAG IS CLEARED ONCE CONSUMED, so a pointer sitting still costs no
       reads at all — and that also keeps the old behaviour to the letter: the
       target moved only on pointermove before, and it moves only on pointermove
       now, scrolling included. */
    let cx = 0;
    let cy = 0;
    let pending = false;

    const onMove = (event) => {
      cx = event.clientX;
      cy = event.clientY;
      pending = true;
    };

    const tick = () => {
      if (pending) {
        const rect = host.getBoundingClientRect();
        tx = cx - rect.left - rect.width / 2;
        ty = cy - rect.top - rect.height / 2;
        pending = false;
      }
      x += (tx - x) * 0.1;
      y += (ty - y) * 0.1;
      el.style.transform =
        'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      if (running) raf = requestAnimationFrame(tick);
    };

    /* CLEANUP CLEARS THE TRANSFORM. This runs mid-life when reduced motion is
       switched on, and stopping the loop alone would leave an off-centre blob
       parked wherever the pointer happened to be — which reads as a rendering
       bug rather than as a light. */
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
      removeEventListener('pointermove', onMove);
      el.style.transform = '';
    };

    const start = () => {
      if (running) return;
      running = true;
      addEventListener('pointermove', onMove, {passive: true});
      raf = requestAnimationFrame(tick);
    };

    const sync = () => {
      if (!touch.matches && !still.matches) start();
      else stop();
    };

    touch.addEventListener('change', sync);
    still.addEventListener('change', sync);
    /* A glow animating in a background tab is a battery bill for nobody. */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else sync();
    });

    sync();
  }

  function bindAll(root) {
    const scope = root instanceof Element ? root : document;
    bindReveals(scope);
    for (const el of scope.querySelectorAll('[data-cursor-light]')) {
      bindCursorLight(el);
    }
  }

  /* --------------------------------------------------------------------------
     HOW MUCH OF US THE THEME'S HEADER COVERS.

     Ella's header is `position: absolute` with z-index 16, so it does NOT take
     space in the flow — it lies ON TOP of whatever section comes first. Measured
     on the demo settings it is 166px tall, because the logo slot is rendering
     the shop name as large text; the moment the owner uploads a real logo that
     number changes. So it is measured rather than written down.

     Two values are published, both on the hero itself:

     --cooper-header-overlap  how far the header group reaches PAST the top of
                              the band. Becomes the band's top padding, which is
                              what keeps the headline out from under the nav.
     --cooper-above           how much flow space sits above the band (the
                              announcement bar, which IS in the flow). The band
                              subtracts it from the viewport height so the two
                              together fill the screen exactly once.

     Both are computed in PAGE coordinates — rect plus scrollY — so the answer
     does not depend on where the visitor happens to be scrolled when this runs.
     ----------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     ONE WALK, NOT TWO. This was two functions — measureHeader() and
     measureHeaderCover() — and they walked THE SAME ELEMENTS with THE SAME
     guards, each doing its own getBoundingClientRect() AND its own
     getComputedStyle() per element. Measured on this store, the header group is
     3 groups of 8 + 97 + 2 = 107 elements, so that was 214 rect+style reads per
     invocation where 107 do the job. IT IS NOW 107.

     THAT WAS THE LARGEST ATTRIBUTABLE MAIN-THREAD REFLOW ON THE SITE. A trace
     ForcedReflow insight on /collections/all at 412x823 with 4x CPU throttling
     and Slow 4G named this file as the top offender: 71ms of reflow time, of
     which measureHeaderCover alone was 67ms.

     THE ITERATION WAS NEVER THE COST, and that is the whole reason the fix is a
     merge rather than a micro-optimisation. Timed in-page unthrottled, one walk
     is 0.2ms and two are 0.5ms. The 67ms was FORCED SYNCHRONOUS LAYOUT, and the
     old shape provoked it by construction: measureHeader() ENDED by writing two
     custom properties onto the hero, which invalidates layout, and
     measureHeaderCover() then OPENED with a getBoundingClientRect(), which has
     to flush that invalidation before it can answer. Read, write, read — the
     classic layout thrash, across a function boundary where nobody could see it.
     Merged, every read happens before either write, so nothing in here reads
     back what it just wrote.

     WHY THE TWO NUMBERS CAN SHARE A WALK: they differ only in what they subtract
     from the same measured bottom edge — the overlap against the hero's top, the
     cover against the group's own flow bottom. Every filter (zero-size,
     visibility, opacity, fixed, sticky, and the 8px "a panel hangs below the
     bar" test) was identical in both, which is why keeping them apart bought
     nothing but a second layout flush.

     AND THE RESIZE HANDLER IS COALESCED, which on a phone matters more than the
     read count. Resize fires when the URL bar collapses during a scroll, so the
     old binding re-ran both walks mid-scroll, on every event, with no debounce
     at all. requestAnimationFrame coalescing collapses a burst into one
     measurement per frame and takes it out of the event handler, so the reflow
     can never block the resize itself. rAF RATHER THAN A 150ms TIMER
     DELIBERATELY: the number this publishes is the hero's top padding, and a
     trailing timer would leave the headline visibly mispositioned for 150ms
     after every orientation change.

     THE HERO IS OPTIONAL AND THAT IS LOAD-BEARING. measureHeader() used to
     return early when there was no .cooper-hero while the cover measurement
     still had to run: on the product page nothing was published and the first
     photograph started at the announcement bar's bottom edge, with the whole
     48px of nav painted on top of it. So the overlap half is skipped when there
     is no hero and the cover half always publishes.
     ----------------------------------------------------------------------- */
  function measureAll() {
    const hero = document.querySelector('.cooper-hero');
    const heroTop = hero ? hero.getBoundingClientRect().top + scrollY : null;

    let overlap = 0;
    let cover = 0;

    /**
     * THE SECTION WRAPPER'S OWN RECT IS USELESS, and that cost a wrong
     * measurement: Ella's header is positioned, so the .shopify-section that
     * contains it collapses to ZERO HEIGHT and its bottom edge sits at its own
     * top. Measured that way the overlap came back 0, the band got 24px of top
     * padding instead of 98, and the headline sat under the nav. So the
     * DESCENDANTS are measured, not the wrapper.
     */
    for (const group of document.querySelectorAll(
      '.shopify-section-group-header-group',
    )) {
      const groupRect = group.getBoundingClientRect();
      const groupTop = groupRect.top + scrollY;

      /* THE NUMBER IS THE OVERLAY, NOT THE HEADER'S HEIGHT. Ella's header group
         keeps the announcement bar in flow and positions the nav row absolutely
         over the content below, so the group's own box already accounts for part
         of what is on screen. What a section has to reserve is only the part
         reaching PAST where the flow resumes: deepest painted edge minus this.
         Measured here that is 78 - 30 = 48, the nav row exactly. */
      const flowBottom = groupRect.bottom + scrollY;

      for (const el of [group, ...group.querySelectorAll('*')]) {
        /* THE ONLY TWO READS IN THIS FUNCTION, one rect and one style, once per
           element. Both numbers below are derived from them. */
        const rect = el.getBoundingClientRect();
        if (rect.height === 0 || rect.width === 0) continue;

        const cs = getComputedStyle(el);
        /* Rendered but not shown: a mega-menu panel moved out of sight by a
           transform reported a bottom edge 290px down the page and pinned the
           overlap to its own sanity cap. */
        if (cs.visibility === 'hidden' || cs.opacity === '0') continue;

        /**
         * A FIXED HEADER HAS NO PAGE POSITION, and this is the second wrong
         * measurement: rect.top + scrollY for a fixed element returns the
         * SCROLL OFFSET, not a place on the page, so scrolling 156px reported an
         * overlap of 242px for a header 86px tall. What a fixed bar covers is
         * simply its own height, wherever the visitor happens to be — and that
         * is true of both numbers, which is why one branch now feeds both.
         */
        if (cs.position === 'fixed') {
          const height = Math.round(rect.height);
          overlap = Math.max(overlap, height);
          cover = Math.max(cover, height);
          continue;
        }

        /**
         * STICKY IS NOT FIXED, and lumping the two together cost a third wrong
         * measurement. A sticky bar KEEPS ITS SPACE IN THE FLOW — the content
         * below it already starts underneath it, so it covers nothing and there
         * is nothing to reserve. Counted as an overlay it added its own height
         * as dead space above the first thing on the page. Ella makes the header
         * sticky whenever the transparent header is off, which is every page
         * here now.
         */
        if (cs.position === 'sticky') continue;

        /* In-flow and absolute bars: only the ones that START at the group's own
           top edge are part of the bar. A panel hangs BELOW it. Eight pixels of
           slack for sub-pixel layout. */
        if (rect.top + scrollY > groupTop + 8) continue;

        /* ONE MEASURED EDGE, TWO ANSWERS. This is the whole reason the merge is
           safe: the two old functions took this identical number and subtracted
           a different origin from it. */
        const bottom = rect.bottom + scrollY;
        if (heroTop !== null) {
          overlap = Math.max(overlap, Math.round(bottom - heroTop));
        }
        cover = Math.max(cover, Math.round(bottom - flowBottom));
      }
    }

    /* CAPPED AT 25% OF THE VIEWPORT. A sanity bound, not a design number: if a
       future header setting renders something tall and visible, the band should
       look wrong by a little rather than push its own headline off the screen. */
    const cap = Math.round(innerHeight * 0.25);

    /* EVERY WRITE IS DOWN HERE, AFTER EVERY READ. Moving these above the loop,
       or splitting this function in two again, reintroduces the 67ms flush. */
    if (hero) {
      hero.style.setProperty(
        '--cooper-header-overlap',
        Math.min(Math.max(0, overlap), cap) + 'px',
      );
      /* --cooper-above: how much flow space sits above the band. The band
         subtracts it from the viewport height so the two together fill the
         screen exactly once. */
      hero.style.setProperty('--cooper-above', Math.round(heroTop) + 'px');
    }

    document.documentElement.style.setProperty(
      '--cooper-header-cover',
      Math.min(Math.max(0, cover), cap) + 'px',
    );
  }

  /* ONE MEASUREMENT PER FRAME, NO MATTER HOW MANY EVENTS ARRIVE. See the note
     above: resize fires repeatedly while a phone's URL bar collapses, and the
     old binding ran the whole walk synchronously inside every one of them. */
  let measureFrame = 0;
  /* A PHONE FIRES `resize` WHILE YOU SCROLL, AND REMEASURING THERE MOVES THE PAGE
     UNDER THE READER'S THUMB. This is the whole of the reported bug: "scrolling
     the home page on mobile, the 3D model glitches and throws you back up or
     slips down."

     THE CHAIN. Scrolling a phone collapses the URL bar, which changes
     window.innerHeight and fires `resize`. measureAll then rewrites two custom
     properties the hero's own box depends on -- --cooper-above, which is
     subtracted from the hero's min-height, and --cooper-header-overlap, which is
     its top padding -- and its sanity cap is innerHeight * 0.25, so the numbers
     genuinely change when only the URL bar moved. The hero's height changes
     mid-gesture, the document gets shorter or taller, and the browser's scroll
     anchoring corrects for it by shifting the scroll position. To the reader that
     is the page jumping back up, or sliding on past where they stopped. The corgi
     is simply the largest thing in the hero, so it is what they notice moving.

     THE GUARD IS ON WIDTH, and width is the right test. A URL bar changes the
     HEIGHT only; a genuine layout change -- rotating the phone, resizing a
     window, a tablet keyboard appearing beside the page -- changes the WIDTH. So
     a resize that leaves the width alone is ignored, and everything that really
     needs a remeasure still gets one. orientationchange is bound as well, because
     a rotation can in principle report the same width on a square viewport.

     THE FIRST CALL STILL RUNS. lastWidth starts at -1, so the parse-time
     measureAll is never skipped -- only later height-only resizes are. */
  var lastWidth = -1;

  function scheduleMeasure(force) {
    if (force !== true && lastWidth === innerWidth) return;
    lastWidth = innerWidth;
    if (measureFrame) return;
    measureFrame = requestAnimationFrame(() => {
      measureFrame = 0;
      measureAll();
    });
  }

  /* THE ORDER OF THESE TWO LINES IS THE FIX, and it is worth a paragraph.

     It used to be measureAll() then bindAll(document), and that is two forced
     layouts rather than one. measureAll ENDS in writes — it sets
     --cooper-header-overlap on the hero and --cooper-header-cover on <html>,
     both document-level custom properties, so the whole document's style is
     dirty when it returns. bindAll then opens with a getBoundingClientRect,
     which cannot answer until that is flushed. Chrome's trace showed the two
     frames separately: measureAll at 136-170ms and bindReveals at 61-75ms.

     Reversed, bindAll's first read forces the layout the browser has to compute
     before first paint anyway, measureAll's reads are then free because nothing
     has dirtied anything, and its writes land at the very end where no read
     follows them. One flush at parse time instead of two.

     DOES THE ORDER CHANGE WHAT IS SHOWN? bindAll's only layout-dependent
     decision is whether a reveal starts above the fold, and it now makes that
     call before the hero's top padding is set. The padding can only push things
     DOWN, so the risk is marking something shown that ends up just below the
     fold — which is harmless, it is visible either way, and the observer
     replays. The reverse cannot happen, and the observer's own first callback
     fires a frame later regardless.

     WHY measureAll STAYS SYNCHRONOUS HERE and is not deferred to a frame: the
     hero's top padding reads --cooper-header-overlap, so a frame's delay is a
     visible jump under the header. Mobile CLS is 0.0005 today and it is 0.0005
     because of this line. The cost is a real layout that has to happen before
     first paint in any case; deferring it would trade a good CLS for a slightly
     better TBT, which is not a trade to make silently. */
  bindAll(document);
  measureAll();
  /* SEED THE WIDTH GUARD HERE, or the very first height-only resize still gets
     through. The parse-time measurement above calls measureAll directly rather
     than going through scheduleMeasure, so lastWidth would still be -1 when a
     phone's URL bar collapses for the first time -- one jump instead of none,
     which is not a fix. */
  lastWidth = innerWidth;

  addEventListener('resize', function () { scheduleMeasure(false); }, {passive: true});
  /* A rotation is a real layout change even in the rare case where the width
     comes back identical, so it forces the measurement past the width guard. */
  addEventListener('orientationchange', function () { scheduleMeasure(true); }, {passive: true});
  /* The logo is an image on a real store, and an image that arrives late changes
     the header's height after this has already run once.

     scheduleMeasure, NOT measureAll: bound directly, the whole walk ran
     synchronously inside the load handler — a second full pass over every
     header descendant at the worst possible moment, and the trace showed it as
     its own 9-10ms forced-layout frame. In a frame it costs the same reads with
     nothing forced. */
  /* FORCED, because by the time `load` fires the width guard above has already
     recorded this width -- and the whole point of this listener is that a late
     logo image changed the header's HEIGHT. Without the flag it would be
     skipped, which is the guard being right about resizes and wrong here. */
  addEventListener('load', function () { scheduleMeasure(true); });

  /* --------------------------------------------------------------------------
     THE THEME EDITOR. It re-renders one section without a page load, and the
     replacement has never been observed by anything above. Without this line
     every section the owner edits sits at opacity 0 in his own editor and he
     reports the section as broken. This is the one requirement with no
     equivalent in the React original.
     ----------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', (event) => {
    bindAll(event.target);
    /* Editing the header, or the hero, changes both numbers. */
    measureAll();
  });
})();
